import { flattenSiteChildren, transformSiteRow } from '../utility';
import { http } from './http';

const baseUrlAPI = '/rollout/api/projectsite';

export const getSchemaProjectList = async (subprojectId) => {
    return http
        .get(`${baseUrlAPI}/${subprojectId}/schema_table/`)
        .then((response) => response.data);
};
export const getDataProjectList = async (subprojectId, queryParams = {}) => {
    return http
        .post(`${baseUrlAPI}/${subprojectId}/load_data/`, queryParams)
        .then((response) => response.data);
};
export const getAdvanceLogicFilters = async (subprojectId) => {
    return http
        .get(`profile/api/advance_logic_filter/details/?subproject=${subprojectId}`)
        .then((response) => response.data);
};
export const postExportAdvanceLogicFilters = async (idExport) => {
    return http
        .post(`profile/api/advance_logic_filter/${idExport}/export/`)
        .then((response) => response.data);
};
export const deleteLines = async (subprojectId, values) => {
    return http
        .delete(`${baseUrlAPI}/${subprojectId}/delete_row/`, {
            data: { rows: values },
        })
        .then((resp) => resp.data);
};

export const disableLines = async (subprojectId, values) => {
    return http
        .post(`${baseUrlAPI}/${subprojectId}/disable_row/`, { rows: values })
        .then((resp) => resp.data);
};

const updateDateRows = async (subprojectId, data) => {
    return http
        .put(`${baseUrlAPI}/${subprojectId}/bulk_update_date_field/`, data)
        .then((resp) => resp.data);
};

const getDateTypeFieldsUserUpdate = async (subprojectId) => {
    return http
        .get(`${baseUrlAPI}/${subprojectId}/bulk_update_date_field/`)
        .then((resp) => resp?.data);
};

export const deleteAllLines = async (subprojectId) => {
    return http.delete(`${baseUrlAPI}/${subprojectId}/delete_data/`).then((resp) => resp.data);
};
export const recoveryLines = async (subprojectId, values) => {
    return http
        .post(`${baseUrlAPI}/${subprojectId}/recovery_row/`, {
            rows: values,
        })
        .then((resp) => resp.data);
};
export const editInline = async (siteId, values) => {
    return http.post(`${baseUrlAPI}/${siteId}/edit_inline/`, values).then((resp) => resp.data);
};

/**
 * Fetches rows related to a site after an inline edit and returns MeliusGrid-ready rows.
 * Uses the same transformation pipeline as generateRowsProjectSite.
 * Some backends return edited row plus subtotal rows in the same payload.
 * Returns an empty rows set on any error (e.g. 404) — callers must handle gracefully.
 *
 * @param {string|number} subprojectId
 * @param {string|number} siteId
 * @param {number}        [delayMs=0] - wait before fetching, to allow DB triggers to propagate
 * @param {Object}        [filters={}] - currently applied search/advance/logic filters, so
 *   subtotals stay consistent with the grid's active query (e.g. `search`, `advance_search`,
 *   `logic_filters`, `filterColumns`, `ordering`)
 * @returns {Promise<{rows: object[], subtotals: object | null}>}
 */
export const fetchRowsBySiteId = async (subprojectId, siteId, delayMs = 0, filters = {}) => {
    try {
        if (delayMs > 0) {
            await new Promise((resolve) => setTimeout(resolve, delayMs));
        }

        const { data } = await http.post(`${baseUrlAPI}/${subprojectId}/load_data/`, {
            ...filters,
            site_id: siteId,
        });
        const flat = data?.sites?.flatMap(flattenSiteChildren) ?? [];

        return {
            rows: flat.map(transformSiteRow),
            subtotals: data?.subtotals ?? null,
        };
    } catch {
        return { rows: [], subtotals: null };
    }
};

export const deleteFilteredDataFromAdvancedSearch = async (subprojectId, filter) => {
    return http
        .delete(`${baseUrlAPI}/${subprojectId}/delete_filtered_data/`, {
            data: filter,
        })
        .then((resp) => resp.data);
};

/**
 * Represents an empty SummaryTable V2 dataset.
 */
const EMPTY_SUMMARY_DATA = {
    periods: [],
    metrics: [],
    rows: [],
};

/**
 * Returns the SummaryTable V2 dataset for a subproject.
 *
 * Uses the summary_calc dataset as the table base and overlays the temporary legacy scenario
 * calculations. Falls back to the local fixture if summary_calc fails or returns no data.
 *
 * @param {string|number} subprojectId - Subproject id used to request summary_calc and R&U rows.
 * @param {string} [region='Nokia Regions'] - RB_Data region name or configured alias.
 * @param {Object} [filters={}] - Active project-list filters; `advance_search` is sent in the summary_calc request body.
 * @returns {Promise<object>} SummaryTable V2 payload returned by the endpoint or fallback fixture.
 * @throws {Error} If the subproject id is missing or scenario composition fails.
 */
export const getSummaryTableV2Data = async (
    subprojectId,
    _region = 'Nokia Regions',
    filters = {}
) => {
    if (subprojectId == null) {
        throw new Error('A subproject id is required to load SummaryTable V2 data.');
    }

    let summaryData = EMPTY_SUMMARY_DATA;

    const requestPayload =
        Object.prototype.hasOwnProperty.call(filters, 'advance_search') && filters.advance_search
            ? { advance_search: filters.advance_search }
            : undefined;

    try {
        const response = await http.post(
            `${baseUrlAPI}/${subprojectId}/summary_calc/`,
            requestPayload
        );

        summaryData = response?.data ?? EMPTY_SUMMARY_DATA;

        return summaryData;
    } catch (error) {
        console.error(
            'Summary Table V2 summary_calc request failed:',
            error?.response?.data ?? error
        );

        return EMPTY_SUMMARY_DATA;
    }
};

/**
 * Fetches the R&U Bucket Summary dataset for a subproject.
 * @param {string|number} subprojectId - Subproject requesting the summary.
 * @param {Object} [_filters] - Current project-list filters reserved for future endpoint support.
 * @returns {Promise<object>} R&U Bucket Summary dataset.
 * @throws {Error} If the subproject id is missing.
 */
export const getRUBucketSummaryData = async (subprojectId, _filters) => {
    if (subprojectId == null) {
        throw new Error('A subproject id is required to load R&U Bucket Summary data.');
    }

    const response = await http.post(`${baseUrlAPI}/${subprojectId}/summary_bucket/`);

    return response.data;
};

const service = {
    getSchemaProjectList,
    getDataProjectList,
    getAdvanceLogicFilters,
    postExportAdvanceLogicFilters,
    disableLines,
    deleteLines,
    deleteAllLines,
    recoveryLines,
    editInline,
    deleteFilteredDataFromAdvancedSearch,
    updateDateRows,
    getDateTypeFieldsUserUpdate,
    getSummaryTableV2Data,
    getRUBucketSummaryData,
};

export default service;
